<template>
  <header
    class="header"
    :class="{
      'header--menu-open': menuActive,
      'header--content-raised': raisedContent
    }"
  >
    <transition name="promotion-reveal">
      <div v-if="!authOnly && promotionVisible" class="header__promotion">
        <a
          ref="promotionLink"
          class="header__promotion-link"
          @click="trackPromotion('click')"
          href="https://www.linkedin.com/feed/update/urn:li:activity:7508088751609434112"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="header__promotion-copy header__promotion-copy--desktop">
            {{ $t('landingPromotion.desktop') }}
          </span>
          <span class="header__promotion-copy header__promotion-copy--mobile">
            {{ $t('landingPromotion.mobile') }}
          </span>
          <span class="header__promotion-cta">{{ $t('landingPromotion.ctaLabel') }}</span>
        </a>
        <button
          class="header__promotion-dismiss"
          type="button"
          :aria-label="$t('landingPromotion.dismissLabel')"
          @click="dismissPromotion"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M6 6l12 12M6 18L18 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </transition>
    <div class="header__links">
      <div class="header__links-left">
        <img src="../images/DRCT.svg" alt="DRCT" class="header__logo" :class="{active: activeUrl('/')}" @click="goToMain(activeUrl('/'))">
        <div v-if="!authOnly" class="header__solutions" @mouseenter="scheduleSolutionsOpen" @mouseleave="scheduleSolutionsClose">
          <button
            ref="solutionsTrigger"
            class="header__solutions-trigger"
            :class="{ 'is-open': solutionsOpen }"
            type="button"
            aria-controls="solutions-menu"
            aria-haspopup="menu"
            :aria-expanded="solutionsOpen"
            @click="toggleSolutions"
          >
            {{$t('solutionsMenu.label')}}<DropdownArrow :open="solutionsOpen" />
          </button>
          <transition name="header-dropdown">
            <section
              v-if="solutionsOpen"
              id="solutions-menu"
              ref="solutionsPanel"
              class="header-dropdown-menu solutions-menu"
              :style="solutionsMenuStyle"
              aria-labelledby="solutions-menu-heading"
            >
              <div class="solutions-menu__intro">
                <h2 id="solutions-menu-heading">{{$t('solutionsMenu.heading')}}</h2>
                <p>{{$t('solutionsMenu.description')}}</p>
              </div>
              <nav class="solutions-menu__grid" :aria-label="$t('solutionsMenu.label')">
                <a
                  v-for="solution in solutions"
                  :key="solution.id"
                  class="solutions-menu__card"
                  :href="solution.href"
                  @click="trackSolution(solution)"
                >
                  <span class="solutions-menu__icons">
                    <svg v-for="(icon, iconIndex) in menuIcons(solution)" :key="iconIndex" class="solutions-menu__icon" width="40" height="40" viewBox="0 0 256 256" aria-hidden="true">
                      <rect width="256" height="256" rx="49" fill="#2a8aea" fill-opacity=".12" />
                      <g transform="translate(128,128) scale(0.56) translate(-128,-128)">
                        <path fill="#2a8aea" :d="icon" />
                      </g>
                    </svg>
                  </span>
                  <h3>{{$t(solution.titleKey)}}</h3>
                  <p>{{$t(solution.descriptionKey)}}</p>
                </a>
              </nav>
            </section>
          </transition>
        </div>
        <div v-if="!authOnly" class="header__products" @mouseenter="scheduleProductsOpen" @mouseleave="scheduleProductsClose">
          <button
            ref="productsTrigger"
            class="header__products-trigger"
            :class="{ 'is-open': productsOpen }"
            type="button"
            aria-controls="products-menu"
            aria-haspopup="menu"
            :aria-expanded="productsOpen"
            @click="toggleProducts"
          >
            {{$t('productsMenu.label')}}<DropdownArrow :open="productsOpen" />
          </button>
          <transition name="header-dropdown">
            <section
              v-if="productsOpen"
              id="products-menu"
              ref="productsPanel"
              class="header-dropdown-menu products-menu"
              :style="productsMenuStyle"
              aria-labelledby="products-menu-heading"
            >
              <div class="solutions-menu__intro">
                <h2 id="products-menu-heading">{{$t('productsMenu.heading')}}</h2>
                <p>{{$t('productsMenu.description')}}</p>
              </div>
              <nav class="solutions-menu__grid" :aria-label="$t('productsMenu.label')">
                <a
                  v-for="product in products"
                  :key="product.id"
                  class="solutions-menu__card"
                  :class="{ 'solutions-menu__card--extension': product.id === 'extension' }"
                  :href="product.href"
                  @click="product.id === 'extension' && navigateToExtension($event)"
                >
                  <span class="solutions-menu__icons">
                    <svg v-for="(icon, iconIndex) in menuIcons(product)" :key="iconIndex" class="solutions-menu__icon" width="40" height="40" viewBox="0 0 256 256" aria-hidden="true">
                      <rect width="256" height="256" rx="49" fill="#2a8aea" fill-opacity=".12" />
                      <g transform="translate(128,128) scale(0.56) translate(-128,-128)">
                        <path fill="#2a8aea" :d="icon" />
                      </g>
                    </svg>
                  </span>
                  <h3>{{$t(product.titleKey)}}</h3>
                  <p>{{$t(product.descriptionKey)}}</p>
                </a>
              </nav>
            </section>
          </transition>
        </div>
      </div>
      <div class="header__links-right">
        <a v-if="!authOnly" class="header__sign-in" href="/sign_in" @click="signIn">{{$t('signIn')}}</a>
        <a
          v-if="!authOnly"
          class="header__try"
          :data-tf-popup="dynamicDemoUrl"
          :data-tf-iframe-props="`title=${$t('try')}`"
          @click="tryClick"
        >
          <span class="header__try-label">{{ displayLinkName }}</span>
        </a>
        <!-- <a
          class="header__try"
          :href="link.url"
          @click="tryClick"
        >
          {{ link.name }}
        </a> -->
        <LanguageSelector
          class="header__language-selector"
          variant="landing"
          :languages="['en', 'ua', 'pl']"
          @languageSelected="saveLangToStarage"
        />
        <button
          ref="mobileMenuTrigger"
          type="button"
          class="header__menu-cross"
          :class="{ active: menuActive }"
          :aria-expanded="menuActive"
          :aria-label="$t('landingNavigation.toggleLabel')"
          @click="toggleMobileMenu"
        >
          <div class="line-1"></div>
          <div class="line-2"></div>
        </button>
      </div>
    </div>
    <MobileMenu
      v-if="menuActive"
      class="header__menu"
      :demo-url="dynamicDemoUrl"
      :show-navigation="!authOnly"
      @tryClick="tryClick"
    />
    <Cookie v-if="!authOnly" />
  </header>
</template>

<script>
import LanguageSelector from '../shared/LanguageSelector.vue'
import DropdownArrow from '../shared/DropdownArrow.vue'
import MobileMenu from './MobileMenu.vue'
import Cookie from './Cookie.vue'
import { solutionLinks } from './solutionLinks.js'
import { productLinks } from './productLinks.js'
import { navigateToExtension } from './extensionNavigation.js'
import { CARD_ICON_OPTIONS } from './WhatYouGain.vue'
import { navigationIconOptions } from './navigation-icon-options.js'
import { AmplitudeEvent } from 'components/shared/amplitude.js';

const ensureTypeformScript = () => {
  if (document.querySelector('script[src*="embed.typeform.com/next/embed.js"]')) return;

  const script = document.createElement('script');
  script.src = '//embed.typeform.com/next/embed.js';
  script.async = true;
  document.head.appendChild(script);
};

const PROMOTION_REVEAL_DELAY = 1000;
const PROMOTION_DISMISSAL_KEY = 'drct-waf-lisbon-2026-promo-dismissed';
// October 16, 2026 at 00:00 in Lisbon (UTC+01:00).
const PROMOTION_ENDS_AT = Date.UTC(2026, 9, 15, 23);
const promotionIsActive = () => Date.now() < PROMOTION_ENDS_AT;

export default {
  components: {
    LanguageSelector,
    DropdownArrow,
    MobileMenu,
    Cookie
  },
  props: {
    authOnly: {
      type: Boolean,
      default: false
    },
    raisedContent: {
      type: Boolean,
      default: false
    }
  },
  data(){
    return {
      menuActive: false,
      promotionVisible: false,
      promotionRevealTimer: null,
      promotionExpiryTimer: null,
      solutionsOpen: false,
      solutionsOpenTimer: null,
      solutionsCloseTimer: null,
      productsOpen: false,
      productsOpenTimer: null,
      productsCloseTimer: null,
      solutionsMenuStyle: {},
      productsMenuStyle: {},
      solutions: solutionLinks,
      products: productLinks,
      menuScrollY: null,
      menuHeaderObserver: null,
      menuScrollLockStyles: null,
      typeformIdleCallback: null,
      typeformTimer: null,
    }
  },
  computed: {
    dynamicDemoUrl() {
      if(this.$i18n.locale == 'pl'){
        return 'iBhXNckk'
      } else {
        return 'CctmLklr'
      }
    },
    displayLinkName() {
      return this.$t("try");
    },
    desktopMenuOpen() {
      return this.solutionsOpen || this.productsOpen
    }
  },
  methods: {
    navigateToExtension(event){
      if (!navigateToExtension(event)) this.closeProducts()
    },
    scheduleSolutionsOpen(){
      this.clearSolutionsCloseTimer()
      if (this.solutionsOpen || this.solutionsOpenTimer !== null) return

      this.solutionsOpenTimer = window.setTimeout(() => {
        this.solutionsOpenTimer = null
        this.openSolutions()
      }, 60)
    },
    openSolutions(){
      this.clearSolutionsOpenTimer()
      this.clearSolutionsCloseTimer()
      this.closeProducts()
      this.menuActive = false
      this.syncSolutionsMenuPosition()
      this.solutionsOpen = true
    },
    toggleSolutions(){
      if (this.solutionsOpen) {
        this.closeSolutions()
        return
      }

      this.openSolutions()
    },
    scheduleSolutionsClose(){
      this.clearSolutionsOpenTimer()
      this.clearSolutionsCloseTimer()
      if (!this.solutionsOpen) return

      this.solutionsCloseTimer = window.setTimeout(() => {
        this.solutionsOpen = false
        this.solutionsCloseTimer = null
      }, 130)
    },
    clearSolutionsOpenTimer(){
      if (this.solutionsOpenTimer !== null) {
        window.clearTimeout(this.solutionsOpenTimer)
        this.solutionsOpenTimer = null
      }
    },
    clearSolutionsCloseTimer(){
      if (this.solutionsCloseTimer !== null) {
        window.clearTimeout(this.solutionsCloseTimer)
        this.solutionsCloseTimer = null
      }
    },
    closeSolutions(){
      this.clearSolutionsOpenTimer()
      this.clearSolutionsCloseTimer()
      this.solutionsOpen = false
    },
    scheduleProductsOpen(){
      this.clearProductsCloseTimer()
      if (this.productsOpen || this.productsOpenTimer !== null) return

      this.productsOpenTimer = window.setTimeout(() => {
        this.productsOpenTimer = null
        this.openProducts()
      }, 60)
    },
    openProducts(){
      this.clearProductsOpenTimer()
      this.clearProductsCloseTimer()
      this.closeSolutions()
      this.menuActive = false
      this.syncProductsMenuPosition()
      this.productsOpen = true
    },
    toggleProducts(){
      if (this.productsOpen) {
        this.closeProducts()
        return
      }

      this.openProducts()
    },
    scheduleProductsClose(){
      this.clearProductsOpenTimer()
      this.clearProductsCloseTimer()
      if (!this.productsOpen) return

      this.productsCloseTimer = window.setTimeout(() => {
        this.productsOpen = false
        this.productsCloseTimer = null
      }, 130)
    },
    clearProductsOpenTimer(){
      if (this.productsOpenTimer !== null) {
        window.clearTimeout(this.productsOpenTimer)
        this.productsOpenTimer = null
      }
    },
    clearProductsCloseTimer(){
      if (this.productsCloseTimer !== null) {
        window.clearTimeout(this.productsCloseTimer)
        this.productsCloseTimer = null
      }
    },
    closeProducts(){
      this.clearProductsOpenTimer()
      this.clearProductsCloseTimer()
      this.productsOpen = false
    },
    syncProductsMenuPosition(){
      const trigger = this.$refs.productsTrigger
      const header = this.$el
      if (!trigger || !header) return

      const headerRect = header.getBoundingClientRect()
      const triggerRect = trigger.getBoundingClientRect()
      const triggerPaddingLeft = parseFloat(window.getComputedStyle(trigger).paddingLeft) || 0
      const panelPaddingLeft = 32
      const viewportGutter = 20
      const desiredLeft = triggerRect.left - headerRect.left + triggerPaddingLeft - panelPaddingLeft
      const left = Math.max(viewportGutter, Math.round(desiredLeft))
      const availableWidth = Math.max(0, Math.floor(headerRect.width - left - viewportGutter))

      this.productsMenuStyle = {
        '--products-menu-left': `${left}px`,
        '--products-menu-width': `${Math.min(880, availableWidth)}px`
      }
    },
    syncSolutionsMenuPosition(){
      const trigger = this.$refs.solutionsTrigger
      const header = this.$el
      if (!trigger || !header) return

      const headerRect = header.getBoundingClientRect()
      const triggerRect = trigger.getBoundingClientRect()
      const triggerPaddingLeft = parseFloat(window.getComputedStyle(trigger).paddingLeft) || 0
      const panelPaddingLeft = 32
      const viewportGutter = 20
      const desiredLeft = triggerRect.left - headerRect.left + triggerPaddingLeft - panelPaddingLeft
      const left = Math.max(viewportGutter, Math.round(desiredLeft))
      const availableWidth = Math.max(0, Math.floor(headerRect.width - left - viewportGutter))

      this.solutionsMenuStyle = {
        '--solutions-menu-left': `${left}px`,
        '--solutions-menu-width': `${Math.min(880, availableWidth)}px`
      }
    },
    closeDesktopMenus(){
      this.closeSolutions()
      this.closeProducts()
    },
    toggleMobileMenu(event) {
      this.closeDesktopMenus();
      this.menuActive = !this.menuActive;
      // Pointer activation should not leave a focus ring on the close icon.
      if (event?.detail > 0) event.currentTarget?.blur();
    },
    schedulePromotionReveal() {
      if (this.authOnly || !this.activeUrl('/') || !promotionIsActive()) return;

      try {
        if (localStorage.getItem(PROMOTION_DISMISSAL_KEY) === 'true') return;
      } catch {
        // Display the announcement when browser storage is unavailable.
      }

      this.promotionRevealTimer = window.setTimeout(() => {
        this.promotionRevealTimer = null;
        if (this.authOnly || !this.activeUrl('/') || !promotionIsActive()) return;

        this.promotionVisible = true;
        this.schedulePromotionExpiry();
        this.$nextTick(() => {
          if (this.promotionVisible) this.trackPromotion('show');
        });
      }, PROMOTION_REVEAL_DELAY);
    },
    clearPromotionRevealTimer() {
      if (this.promotionRevealTimer === null) return;

      window.clearTimeout(this.promotionRevealTimer);
      this.promotionRevealTimer = null;
    },
    trackPromotion(action) {
      try {
        const link = this.$refs.promotionLink;
        if (!link) return;

        const copy = Array.from(link.querySelectorAll('.header__promotion-copy'))
          .find(element => window.getComputedStyle(element).display !== 'none');
        new AmplitudeEvent('Landing banner', {
          action,
          message: copy?.textContent.trim(),
          url: link.href,
          language: this.$i18n.locale
        });
      } catch {
        // Analytics must not interrupt banner navigation or dismissal.
      }
    },
    dismissPromotion() {
      this.trackPromotion('close');
      this.clearPromotionRevealTimer();
      this.clearPromotionExpiryTimer();
      this.promotionVisible = false;

      try {
        localStorage.setItem(PROMOTION_DISMISSAL_KEY, 'true');
      } catch {
        // Keep the current-page dismissal working when storage is unavailable.
      }
    },
    schedulePromotionExpiry() {
      const remaining = PROMOTION_ENDS_AT - Date.now();

      if (remaining <= 0) {
        this.promotionVisible = false;
        return;
      }

      this.promotionExpiryTimer = window.setTimeout(() => {
        this.promotionExpiryTimer = null;
        this.promotionVisible = false;
      }, remaining);
    },
    clearPromotionExpiryTimer() {
      if (this.promotionExpiryTimer === null) return;

      window.clearTimeout(this.promotionExpiryTimer);
      this.promotionExpiryTimer = null;
    },
    lockPageScroll(){
      if (this.menuScrollLockStyles) return

      const body = document.body
      const html = document.documentElement
      const scrollY = window.scrollY || window.pageYOffset || 0
      const scrollbarWidth = window.innerWidth - html.clientWidth

      this.menuScrollY = scrollY
      this.menuScrollLockStyles = {
        body: {
          overflow: body.style.overflow,
          paddingRight: body.style.paddingRight
        },
        html: {
          overflow: html.style.overflow,
          overscrollBehavior: html.style.overscrollBehavior
        }
      }

      body.style.overflow = 'hidden'
      if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`
      html.style.overflow = 'hidden'
      html.style.overscrollBehavior = 'none'
    },
    unlockPageScroll(){
      if (!this.menuScrollLockStyles) return

      const { body: bodyStyles, html: htmlStyles } = this.menuScrollLockStyles
      const scrollY = this.menuScrollY || 0

      Object.assign(document.body.style, bodyStyles)
      Object.assign(document.documentElement.style, htmlStyles)
      this.menuScrollY = null
      this.menuScrollLockStyles = null
      if (Math.round(window.scrollY) !== Math.round(scrollY)) {
        window.scrollTo(0, scrollY)
      }
    },
    syncMenuViewport(){
      const viewport = window.visualViewport
      const viewportHeight = viewport?.height || window.innerHeight
      const headerHeight = Math.round(this.$el?.getBoundingClientRect().height || 54);
      const menuHeight = Math.max(0, Math.round(viewportHeight - headerHeight));

      this.$el?.style.setProperty('--mobile-menu-top', `${headerHeight}px`);
      this.$el?.style.setProperty('--mobile-menu-height', `${menuHeight}px`)
    },
    startMenuViewportSync(){
      this.syncMenuViewport()
      this.menuHeaderObserver = new ResizeObserver(() => this.syncMenuViewport())
      this.menuHeaderObserver.observe(this.$el)
      window.visualViewport?.addEventListener('resize', this.syncMenuViewport)
      window.visualViewport?.addEventListener('scroll', this.syncMenuViewport)
    },
    stopMenuViewportSync(){
      this.menuHeaderObserver?.disconnect()
      this.menuHeaderObserver = null
      window.visualViewport?.removeEventListener('resize', this.syncMenuViewport)
      window.visualViewport?.removeEventListener('scroll', this.syncMenuViewport)
      this.$el?.style.removeProperty('--mobile-menu-top');
      this.$el?.style.removeProperty('--mobile-menu-height')
    },
    handleViewportResize(){
      if (window.innerWidth <= 767) this.closeDesktopMenus()
      if (window.innerWidth > 767 && this.menuActive) this.menuActive = false
      if (this.solutionsOpen) this.syncSolutionsMenuPosition()
      if (this.productsOpen) this.syncProductsMenuPosition()
    },
    menuIcons(item){
      return navigationIconOptions.menu[item.id] || [CARD_ICON_OPTIONS[item.iconKey]]
    },
    trackSolution(solution){
      if (solution.analytics === 'airlines') {
        this.forAirlines()
      } else {
        this.forAgencies()
      }
    },
    handleClickOutside(event){
      if (!this.desktopMenuOpen) return

      const menuPairs = [
        [this.$refs.solutionsPanel, this.$refs.solutionsTrigger],
        [this.$refs.productsPanel, this.$refs.productsTrigger]
      ]
      const clickedInsideMenu = menuPairs.some(([panel, trigger]) =>
        panel?.contains(event.target) || trigger?.contains(event.target)
      )

      if (!clickedInsideMenu) {
        this.closeDesktopMenus()
      }
    },
    handleKeydown(event){
      if (event.key !== 'Escape') return

      if (this.solutionsOpen) {
        this.closeSolutions()
        this.$nextTick(() => this.$refs.solutionsTrigger?.focus())
      } else if (this.productsOpen) {
        this.closeProducts()
        this.$nextTick(() => this.$refs.productsTrigger?.focus())
      } else if (this.menuActive) {
        this.menuActive = false
        this.$nextTick(() => this.$refs.mobileMenuTrigger?.focus())
      }
    },
    saveLangToStarage(lang){
      if (this.lsAvailable()){
        localStorage.setItem("drct-lang", lang)
      }
    },
    setDefaultLang(){
      if (this.lsAvailable()){
        if (localStorage.getItem("drct-lang") === null) {
          const userLang = navigator.language || navigator.userLanguage
          if (["ua", "ru"].some(el => userLang.includes(el))) {
            localStorage.setItem("drct-lang", "en")
            this.$i18n.locale = "en"
          } else if (["pl", "pl-PL"].some(el => userLang.includes(el))) {
            localStorage.setItem("drct-lang", "pl")
            this.$i18n.locale = "pl"
          } else {
            localStorage.setItem("drct-lang", "en")
            this.$i18n.locale = "en"
          }
        }
      }
    },
    lsAvailable(){
        const test = 'test';
        try {
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch(e) {
            return false;
        }
    },
    activeUrl(url){
      return location.pathname == url
    },
    goToMain(isActive){
      if (!isActive){
        window.location.replace([window.location.origin, ''].join('/'));
      }
    },
    tryClick(){
      ensureTypeformScript();
      new AmplitudeEvent("Try", {spot: "top"})
    },
    signIn(){
      new AmplitudeEvent("Sign In")
    },
    forAirlines(){
      new AmplitudeEvent("For airlines", { spot: "top" })
    },
    forAgencies(){
      new AmplitudeEvent("For agents", { spot: "top" })
    }
  },
  watch: {
    promotionVisible() {
      if (this.menuActive) this.$nextTick(() => this.syncMenuViewport())
    },
    menuActive(isOpen){
      if (isOpen) {
        this.lockPageScroll()
        this.startMenuViewportSync()
      } else {
        this.stopMenuViewportSync()
        this.unlockPageScroll()
      }
    },
    desktopMenuOpen(isOpen){
      if (isOpen) {
        document.addEventListener('mousedown', this.handleClickOutside, true)
      } else {
        document.removeEventListener('mousedown', this.handleClickOutside, true)
      }
    }
  },
  mounted(){
    if (!this.authOnly) {
      const loadTypeform = () => {
        this.typeformIdleCallback = null;
        this.typeformTimer = null;
        ensureTypeformScript();
      };

      if (window.requestIdleCallback) {
        this.typeformIdleCallback = window.requestIdleCallback(loadTypeform, { timeout: 1800 });
      } else {
        this.typeformTimer = window.setTimeout(loadTypeform, 750);
      }

      document.querySelector('.wrapper').classList.add('wrapper--disabled');
      document.querySelector('meta[name="viewport"]').setAttribute("content", "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no");
      this.schedulePromotionReveal();
    }

    document.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('resize', this.handleViewportResize)
    this.setDefaultLang()
  },
  beforeUnmount(){
    if (this.typeformIdleCallback) window.cancelIdleCallback?.(this.typeformIdleCallback)
    if (this.typeformTimer) window.clearTimeout(this.typeformTimer)
    this.clearPromotionRevealTimer()
    this.clearPromotionExpiryTimer()
    this.clearSolutionsOpenTimer()
    this.clearSolutionsCloseTimer()
    this.clearProductsOpenTimer()
    this.clearProductsCloseTimer()
    this.stopMenuViewportSync()
    this.unlockPageScroll()
    document.removeEventListener('mousedown', this.handleClickOutside, true)
    document.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('resize', this.handleViewportResize)
  },
}
</script>

<style lang="scss" scoped>
@use './demo-button' as *;

.header {
  --mobile-menu-gutter: 20px;
  height: auto;
  background: white;
  text-align: center;
  box-shadow: 0 4px 8px 0 rgba(27, 39, 51, 0.1);
  position: sticky;
  top: 0;
  z-index: 6;

  .header__logo {
    font-size: 18px;
    font-weight: bold;
    letter-spacing: 0.3px;
    color: #2a8aea;
    display: inline-block;
    transform: none;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    user-select: none;

    &:not(.active) {
      cursor: pointer;
    }
  }

  .language-selector {
    line-height: initial;
    @media (max-width: 767px) {
      display: none;
    }
  }
}

.header__promotion {
  --promotion-bg-start: #c9e7ff;
  --promotion-bg-middle: #9bcfff;
  --promotion-bg-end: #78b9ff;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  margin-bottom: 0;
  color: #1b2733;
  background: linear-gradient(
    100deg,
    var(--promotion-bg-start) 0%,
    var(--promotion-bg-middle) 52%,
    var(--promotion-bg-end) 100%
  );
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.01em;
  line-height: 18px;
}

// The strip opens its own space so the navigation moves with it.
.promotion-reveal-enter-active,
.promotion-reveal-leave-active {
  overflow: hidden;
  transition: height 250ms cubic-bezier(0.16, 1, 0.3, 1), opacity 200ms ease-out;
}

.promotion-reveal-leave-active {
  pointer-events: none;
  transition-duration: 200ms, 150ms;
}

.promotion-reveal-enter-from,
.promotion-reveal-leave-to {
  height: 0;
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .promotion-reveal-enter-active,
  .promotion-reveal-leave-active {
    transition: opacity 150ms ease-out;
  }
}

.header__promotion-link {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 100%;
  gap: 8px;
  color: inherit;
  text-align: center;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid rgba(27, 39, 51, 0.7);
    outline-offset: -3px;
  }
}

.header__promotion-copy {
  white-space: nowrap;
}

.header__promotion-copy--mobile {
  display: none;
}

.header__promotion-cta {
  flex: 0 0 auto;
  text-decoration-line: underline;
  text-decoration-color: rgba(27, 39, 51, 0.32);
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  white-space: nowrap;
}

.header__promotion-dismiss {
  display: inline-flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  color: inherit;
  background: transparent;
  border: 0;
  cursor: pointer;

  > svg {
    // Optical alignment within the announcement strip.
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(27, 39, 51, 0.7);
    outline-offset: -2px;
  }

}

.header--menu-open {
  position: fixed;
  right: 0;
  left: 0;
  width: 100%;

  @media (pointer: coarse) {
    touch-action: none;
  }
}

.header__links {
  width: 1010px;
  height: 54px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    width: 100%;
    padding: 0 var(--mobile-menu-gutter);
  }

}

.header__links-left {
  display: flex;
  align-items: center;
  line-height: 60px;

  .header__solutions,
  .header__products {
    display: inline-flex;
    align-items: center;
  }

  > a,
  > .header__solutions > .header__solutions-trigger,
  > .header__products > .header__products-trigger {
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    padding: 0 10px;
    border: 0;
    border-radius: 8px;
    line-height: 1;
    transform: none;
    cursor: pointer;
    text-transform: uppercase;
    opacity: .5;
    font-size: 13px;
    font-weight: bold;
    color: rgb(27, 39, 51);
    margin-left: 10px;
    text-decoration: none;
    transition: background .14s ease, color .14s ease, opacity .14s ease;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    user-select: none;

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &:focus-visible {
      background: rgba(27, 39, 51, .06);
      color: rgb(27, 39, 51);
      opacity: 1;
    }

    &:focus-visible {
      outline: 2px solid rgba(42, 138, 234, .55);
      outline-offset: 2px;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: rgba(27, 39, 51, .06);
        color: rgb(27, 39, 51);
        opacity: 1;
      }
    }

    &.active {
      opacity: 1;
    }

    @media (max-width: 767px) {
      display: none;
    }
  }

  > .header__solutions > .header__solutions-trigger,
  > .header__products > .header__products-trigger {
    cursor: default;
  }

  /* Keep the logo visually separate while tightening the related menu pair. */
  > .header__solutions > .header__solutions-trigger {
    margin-left: 20px;
  }

  > .header__products > .header__products-trigger {
    margin-left: 6px;
  }

  .header__solutions-trigger,
  .header__products-trigger {
    appearance: none;
    -webkit-appearance: none;
    display: inline-flex;
    align-items: center;
    background: transparent;
    font-family: inherit;
    gap: 0;
    min-height: 30px;

    &.is-open {
      background: rgba(27, 39, 51, .06);
      color: rgb(27, 39, 51);
      opacity: 1;
    }

    &:focus-visible {
      outline: 2px solid rgba(42, 138, 234, .55);
      outline-offset: 2px;
    }
  }

  @media (min-width: 768px) and (max-width: 1120px) {
    > a,
    > .header__solutions > .header__solutions-trigger,
    > .header__products > .header__products-trigger {
      font-size: 12px;
      margin-left: 8px;
      padding: 0 8px;
    }

    > .header__solutions > .header__solutions-trigger {
      margin-left: 18px;
    }

    > .header__products > .header__products-trigger {
      margin-left: 5px;
    }
  }
}

.header__links-right {
  display: flex;
  align-items: center;
  line-height: 60px;
  transform: translateY(1px);

  a {
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    box-sizing: border-box;
    padding: 0 9px;
    border: 0;
    border-radius: 8px;
    line-height: 1;
    font-size: 13px;
    font-weight: bold;
    letter-spacing: 0.6px;
    color: #2a8aea;
    text-transform: uppercase;
    margin: 0 10px 0 0;
    cursor: pointer;
    opacity: .8;
    text-decoration: none;
    transition: background .14s ease, color .14s ease, opacity .14s ease;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    user-select: none;

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &:focus-visible {
      background: rgba(42, 138, 234, .09);
      opacity: 1;
    }

    &:focus-visible {
      outline: 2px solid rgba(42, 138, 234, .55);
      outline-offset: 2px;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: rgba(42, 138, 234, .09);
        opacity: 1;
      }
    }
  }

  .header__try {
    @include demo-button;
    display: block;
    color: white;
    opacity: 0.9;
    background-color: rgb(42, 138, 234);
    align-self: flex-start;
    margin: 7px 20px 0 0;
    flex-shrink: 0;

    .header__try-label {
      display: inline-block;
      transform: translateY(-1px);
    }

    &:hover,
    &:active {
      background: #1a7dd4;
      box-shadow: 0 4px 14px rgba(42, 138, 234, 0.3);
      opacity: 1;
    }

  }

  ::v-deep(.language-selector__current) {
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    cursor: default;
    line-height: 1;
    opacity: .5;
    transition: background .14s ease, opacity .14s ease;

    p {
      display: inline-flex;
      align-items: center;
      margin: 0;
      position: relative;
      isolation: isolate;
      line-height: 1;

      &::before {
        position: absolute;
        z-index: -1;
        inset: -8px -9px;
        border-radius: 8px;
        background: transparent;
        content: '';
        transition: background .14s ease;
      }
    }

    &:hover {
      background: transparent;
      opacity: 1;

      p::before {
        background: rgba(27, 39, 51, .06);
      }
    }
  }

  ::v-deep(.language-selector.open .language-selector__current) {
    background: transparent;
    opacity: 1;

    p::before {
      background: rgba(27, 39, 51, .06);
    }
  }

  @media (min-width: 768px) {
    .header__sign-in,
    ::v-deep(.language-selector__current) {
      transform: translateY(-1px);
    }
  }

}

.header__menu-cross {
  border: 0;
  padding: 0;
  background: transparent;
  transition: 250ms opacity 200ms cubic-bezier(0.19, 1, 0.22, 1), 250ms transform 200ms cubic-bezier(0.19, 1, 0.22, 1);
  width: 24px;
  height: 12px;
  position: relative;
  cursor: pointer;
  display: none;
  -webkit-appearance: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid rgba(42, 138, 234, .55);
    outline-offset: 2px;
  }

  @media (max-width: 767px) {
    display: block;
  }

  & > div {
    opacity: 0.85;
    background-color: #1b2733;
    transition: 500ms all cubic-bezier(0.19, 1, 0.22, 1);
    height: 2px;
    transform-origin: 50% 50%;
    position: absolute;
    width: 100%;
  }

  .line-1 {
    top: 0;
  }

  .line-2 {
    bottom: 0;
  }

  &.active {
    .line-1 {
      transform: rotate(45deg) translate(3px, 4px);
    }

    .line-2 {
      transform: rotate(-45deg) translate(3px, -4px);
    }
  }
}

.header__menu {
  @media (min-width: 768px) {
    display: none;
  }
}

@media (max-width: 767px) {
  .header__links-left .header__solutions-trigger,
  .header__links-left .header__products-trigger {
    display: none;
  }
}

@media (max-width: 767px) {
  .header__promotion {
    font-size: clamp(10.5px, 3.4vw, 13px);
    letter-spacing: 0;
  }

  .header__promotion-link {
    gap: 6px;
  }

  .header__promotion-copy--desktop {
    display: none;
  }

  .header__promotion-copy--mobile {
    display: inline;
  }
}

@media (max-width: 767px) {
  .header {
    --mobile-menu-gutter: 12px;
  }

  .header__links {
    gap: 8px;
    padding: 0 var(--mobile-menu-gutter);
  }

  .header__links-left {
    flex: 0 0 auto;
    min-width: 0;
    line-height: normal;
  }

  .header .header__logo {
    width: clamp(76px, 25vw, 94px);
    height: auto;
  }

  .header__links-right {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    justify-content: flex-end;
    gap: clamp(4px, 2vw, 8px);
    line-height: normal;
    transform: none;

    a {
      flex-shrink: 0;
      margin: 0;
    }

    .header__sign-in {
      transform: none;
      min-height: 34px;
      padding: 0 3px;
      font-size: clamp(11px, 3vw, 13px);
      letter-spacing: clamp(.2px, .12vw, .6px);
      white-space: nowrap;
    }

    .header__try {
      display: inline-flex;
      align-self: center;
      height: 34px;
      padding: 0 clamp(6px, 2vw, 10px);
      margin: 0;
      font-size: clamp(10px, 3vw, 12px);
      letter-spacing: clamp(.1px, .1vw, .35px);
      line-height: 1;
      border-radius: 8px;
    }

    @media (min-width: 340px) {
      .header__sign-in {
        min-height: 32px;
        padding: 0 9px;
        font-size: 13px;
        letter-spacing: .6px;
      }

      .header__try {
        display: block;
        align-self: flex-start;
        height: 38px;
        padding: 10px 20px;
        margin: 8px 0 0 5px;
        font-size: 13px;
        letter-spacing: .6px;
        line-height: 20px;
        border-radius: 10px;
      }
    }
  }

  .header__menu-cross {
    display: block;
    flex: 0 0 32px;
    width: 32px;
    height: 34px;

    & > div {
      left: 4px;
      width: 24px;
      top: 50%;
      bottom: auto;
      transition: transform .14s ease;
    }

    .line-1 {
      top: 50%;
      bottom: auto;
      transform: translateY(-5px);
    }

    .line-2 {
      top: 50%;
      bottom: auto;
      transform: translateY(5px);
    }

    &.active {
      .line-1 {
        transform: rotate(45deg);
      }

      .line-2 {
        transform: rotate(-45deg);
      }
    }

    &:not(.active) {
      .line-1 {
        transform: translateY(-6px);
      }

      .line-2 {
        transform: translateY(4px);
      }
    }
  }
}

@media (max-width: 767px) {
  .header__links-right .header__try {
    display: none;
  }
}

@media (max-width: 359px) {
  .header {
    --mobile-menu-gutter: 8px;
  }

  .header__links {
    gap: 6px;
    padding: 0 var(--mobile-menu-gutter);
  }
}

.header-dropdown-menu {
  --header-dropdown-translate-x: -50%;
  position: absolute;
  top: calc(100% + 12px);
  /* A shared anchor keeps every desktop dropdown on the same left edge. */
  left: calc(50% + 17px);
  width: min(880px, calc(100vw - 40px));
  padding: 28px 32px 30px;
  background: white;
  border-radius: 14px;
  box-shadow: 0 18px 42px rgba(27, 39, 51, .16);
  text-align: left;
  transform: translateX(var(--header-dropdown-translate-x));
  z-index: 12;

  &::before {
    position: absolute;
    top: -12px;
    right: 0;
    left: 0;
    height: 12px;
    content: '';
  }

}

.products-menu {
  --header-dropdown-translate-x: 0%;
  left: var(--products-menu-left, calc(50% + 17px));
  width: var(--products-menu-width, min(880px, calc(100vw - 40px)));
}

.solutions-menu {
  --header-dropdown-translate-x: 0%;
  left: var(--solutions-menu-left, calc(50% + 17px));
  width: var(--solutions-menu-width, min(880px, calc(100vw - 40px)));

  &__intro {
    padding-bottom: 22px;
    border-bottom: 1px solid rgba(27, 39, 51, .1);

    h2,
    p {
      margin: 0;
    }

    h2 {
      color: #1b2733;
      font-size: 22px;
      font-weight: 700;
      line-height: 1.2;
    }

    p {
      max-width: 660px;
      margin-top: 8px;
      color: rgba(27, 39, 51, .62);
      font-size: 15px;
      line-height: 1.5;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 5px 18px;
    /* Offset the card padding so its copy retains the intro's left edge. */
    margin: 3px -16px -16px;
    padding-top: 12px;
  }

  &__card {
    display: block;
    min-height: 142px;
    box-sizing: border-box;
    padding: 16px;
    color: #1b2733;
    text-decoration: none;
    border-radius: 10px;
    transition: background .14s ease;

    h3,
    p {
      margin: 0;
    }

    h3 {
      color: #1b2733;
      font-size: 16px;
      font-weight: 700;
      line-height: 1.3;
    }

    .solutions-menu__icons {
      display: flex;
      gap: 8px;
      margin-bottom: 11px;
    }

    .solutions-menu__icon {
      display: block;
      flex-shrink: 0;
    }

    p {
      margin-top: 7px;
      color: rgba(27, 39, 51, .6);
      font-size: 14px;
      line-height: 1.45;
    }

    &:hover,
    &:focus-visible {
      background: rgba(42, 138, 234, .09);
    }

    &:focus-visible {
      outline: 2px solid rgba(42, 138, 234, .55);
      outline-offset: 2px;
    }
  }

}

.header-dropdown-menu {
  @media (max-width: 767px) {
    display: none;
  }
}


/* Keep the selected card's neutral appearance when navigation clears :hover. */
.solutions-menu__card--extension {
  &:hover,
  &:focus-visible,
  &:active,
  &[data-extension-opening] {
    background: #f0f1f2;
    outline-color: rgba(27, 39, 51, .35);

    .solutions-menu__icon path,
    .solutions-menu__icon rect {
      fill: #1b2733;
    }
  }

  &[data-extension-opening] {
    transition: none;
    outline: none;
  }
}

.header-dropdown-enter-active,
.header-dropdown-leave-active {
  transition: opacity .17s ease, transform .17s ease;
}

.header-dropdown-enter-from,
.header-dropdown-leave-to {
  opacity: 0;
  transform: translateX(var(--header-dropdown-translate-x)) translateY(-4px);
}

.header-dropdown-enter-to,
.header-dropdown-leave-from {
  opacity: 1;
  transform: translateX(var(--header-dropdown-translate-x)) translateY(0);
}
</style>
